import { ActionIcon, Badge, Button, Group, Menu, Stack, Text } from "@mantine/core";
import {
  IconArrowDown,
  IconArrowUp,
  IconEdit,
  IconPlus,
  IconQrcode,
  IconTextSize,
  IconTrash,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import type { LabelElement, LabelType } from "../../api/types";
import { ALLOWED_FIELDS } from "./constants";

interface ElementListProps {
  labelType: LabelType;
  elements: LabelElement[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAdd: (el: LabelElement) => void;
  onDelete: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
}

function newId(): string {
  return crypto.randomUUID().replace(/-/g, "");
}

function elementSummary(el: LabelElement): string {
  if (el.kind === "qr") return `QR · ${el.field}`;
  if (el.source === "static") return `${el.content ?? ""}`;
  return `{${el.field}}`;
}

export function ElementList({
  labelType,
  elements,
  selectedId,
  onSelect,
  onAdd,
  onDelete,
  onMoveUp,
  onMoveDown,
}: ElementListProps) {
  const { t } = useTranslation();
  const fields = ALLOWED_FIELDS[labelType];

  function addStaticText() {
    onAdd({
      id: newId(), kind: "text", source: "static", content: "Text",
      field: null, x: 50, y: 50, font: "scalable", scale: 14,
      bold: false, rotation: 0,
    });
  }
  function addField(field: string) {
    onAdd({
      id: newId(), kind: "text", source: "field", content: null,
      field, x: 50, y: 50, font: "scalable", scale: 14,
      bold: false, rotation: 0,
    });
  }
  function addQr(field: string) {
    onAdd({
      id: newId(), kind: "qr", source: "field", field, x: 100, y: 50, scale: 4,
    });
  }

  return (
    <Stack gap="xs">
      <Group gap="xs">
        <Menu shadow="md" position="bottom-start">
          <Menu.Target>
            <Button size="xs" leftSection={<IconPlus size={14} />}>
              {t("labelEditor.addText")}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={addStaticText} leftSection={<IconTextSize size={14} />}>
              {t("labelEditor.addStaticText")}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Label>{t("labelEditor.addFieldText")}</Menu.Label>
            {fields.map((f) => (
              <Menu.Item key={f} leftSection={<IconEdit size={14} />} onClick={() => addField(f)}>
                {f}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>

        <Menu shadow="md" position="bottom-start">
          <Menu.Target>
            <Button size="xs" variant="light" leftSection={<IconQrcode size={14} />}>
              {t("labelEditor.addQr")}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {fields.map((f) => (
              <Menu.Item key={f} leftSection={<IconQrcode size={14} />} onClick={() => addQr(f)}>
                {f}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </Group>

      {elements.length === 0 ? (
        <Text size="sm" c="dimmed">{t("labelEditor.noElements")}</Text>
      ) : (
        elements.map((el, idx) => (
          <Group
            key={el.id}
            wrap="nowrap"
            gap="xs"
            onClick={() => onSelect(el.id)}
            style={{
              cursor: "pointer",
              padding: "6px 8px",
              border: el.id === selectedId ? "1px solid #228be6" : "1px solid #e5e5e5",
              borderRadius: 4,
              background: el.id === selectedId ? "rgba(34,139,230,.08)" : "transparent",
            }}
          >
            <Badge size="xs" variant="light" color={el.kind === "qr" ? "grape" : "blue"}>
              {el.kind}
            </Badge>
            <Text size="sm" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
              {elementSummary(el)}
            </Text>
            <ActionIcon
              size="sm" variant="subtle" disabled={idx === 0}
              onClick={(e) => { e.stopPropagation(); onMoveUp(el.id); }}
              aria-label="up"
            >
              <IconArrowUp size={14} />
            </ActionIcon>
            <ActionIcon
              size="sm" variant="subtle" disabled={idx === elements.length - 1}
              onClick={(e) => { e.stopPropagation(); onMoveDown(el.id); }}
              aria-label="down"
            >
              <IconArrowDown size={14} />
            </ActionIcon>
            <ActionIcon
              size="sm" variant="subtle" color="red"
              onClick={(e) => { e.stopPropagation(); onDelete(el.id); }}
              aria-label="delete"
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        ))
      )}
    </Stack>
  );
}
